<script setup>
import { computed, ref, watch } from 'vue'
import { result as _result } from 'lodash-es'
import { storeToRefs } from 'pinia'
import { commons } from '@provise/lib/transformer'
import { useConfigsStore } from '@/stores/configs'
import { useComplaintStore } from '@/stores/complaint'

import List from '@provise/components/list/list.vue'
import ListTh from '@provise/components/list/th.vue'

const emit = defineEmits(['select'])
const props = defineProps({
  exclude: {
    type: Array,
  },
})

const { config } = storeToRefs(useConfigsStore())
const { model, query } = storeToRefs(useComplaintStore())
const tdStyle = ref({})
const lm = ref({})
const rows = computed(() => model.value.data || [])
const total = computed(() => model.value.total_count || 0)

watch(lm, lmv => {
  if (props.exclude) {
    lmv.exclude = props.exclude
  } else {
    delete lmv.exclude
  }
  query.value = lmv
})

const downloadXlsUrl = computed(() => {
  let filter = { ...(lm.value || {}) }
  filter['format'] = 'xls'
  delete filter.onPage
  delete filter.page

  const params = new URLSearchParams(filter).toString()
  return 'api/v1.0/export_complaints?' + params
})

const tableHeight = computed(() => {
  if (model.value.total_count < 6) {
    const margin = 6 + (5 - total.value) * 3 + 'em'
    return margin
  }
  return `auto`
})

const changeTdStyle = isOpen => {
  if (isOpen) {
    tdStyle.value = { position: 'static', mask: 'unset' }
  } else {
    tdStyle.value = {}
  }
}

const columns = [
  { title: 'id', label: '№' },
  {
    title: 'medicineLabel',
    label: 'Лекарственный препарат',
    path: 'medicine.medicine_trademark.name',
  },
  {
    title: 'request_category.parent.name',
    label: 'Категория',
    getter: o => (o.close ? 'Запросы' : _result(o, 'request_category.parent.name')),
  },
  {
    title: 'request_category.name',
    label: 'Тип',
    getter: o => (o.close ? 'Прочие запросы' : _result(o, 'request_category.name')),
  },
  {
    title: 'current_sla',
    label: 'Дата завершения этапа',
    path: 'current_sla.date_finish',
    getter: o => {
      let date
      if (o.close) {
        date = o.date_closed
      } else {
        date = o.current_sla?.date_real_finish || o.current_sla?.date_finish
      }
      return commons.dateLocal(date, 'Pp')
    },
  },
  { title: 'status.value', label: 'Этап', path: 'status.value' },
  {
    title: 'assign_user',
    label: 'Ответственный',
    path: 'assign_user.fio',
    getter: o => o.assign_user?.fio || o.assign_user?.username,
  },
]
</script>
<template>
  <List v-model="lm" :total="total">
    <template #before-filter>
      <div class="hstack gap-1" v-if="total">
        <button
          v-if="!modalstack"
          type="button"
          class="btn btn-outline-primary"
          @click="$router.push({ name: 'complaint.create' })"
        >
          <x-icon icon="plus" />
        </button>
        <a
          class="btn btn-outline-primary text-nowrap"
          download="complaints.xls"
          :href="downloadXlsUrl"
        >
          <x-icon icon="fas fa-file-excel" />
        </a>
      </div>
      <template v-else>
        <button
          v-if="!modalstack"
          type="button"
          class="btn btn-outline-primary"
          @click="$router.push({ name: 'complaint.create' })"
        >
          <x-icon icon="plus" />
        </button>
      </template>
    </template>
    <div class="scrollable-table mb-3">
      <table
        class="table table-striped table-hover table-w-controls"
        :style="{ 'margin-bottom': tableHeight }"
      >
        <thead>
          <tr>
            <ListTh
              v-for="column in columns"
              :key="column.title"
              :cfg="config.customer_category"
              :column-id="column.path || column.title"
              :width="column.width"
              @change-td-style="changeTdStyle"
            >
              {{ column.label }}
            </ListTh>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in rows"
            :key="row.id"
            class="pointer"
            :class="{ 'text-danger': row.overdue }"
            @click="$emit('select', row)"
          >
            <td v-for="(col, name) in columns" :key="name">
              <div :style="col.width ? 'width:' + col.width : ''">
                <template v-if="col.getter">
                  {{ col.getter(row) }}
                </template>
                <template v-else-if="col.path == 'current_sla.date_finish'">
                  {{ row[col.title] }}
                  <i class="bi bi-exclamation-circle text-danger" v-if="row.overdue" />
                </template>
                <template v-else-if="col.title == 'medicineLabel'">
                  {{ row[col.title] }}
                </template>
                <template v-else-if="col.date && typeof _result(row, col.path) === 'string'">
                  {{ commons.dateLocal(_result(row, col.path)) }}
                </template>
                <template v-else>
                  {{ _result(row, col.path || col.title) }}
                </template>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </List>
</template>
